import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback } from 'react';
import apiClient from '../api/client';

type UserRole = 'USER' | 'ADMIN';

interface User {
    userId: string;
    name: string;
    email: string;
    role: UserRole;
    balance: number;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<any>;
    register: (username: string, email: string, password: string) => Promise<any>;
    logout: () => void;
    updateBalance: (newBalance: number) => void;
    refreshUser: () => void;
    updateToken: (newToken: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0); // ✨ CORREÇÃO: Reintroduzindo o gatilho

    // Função para forçar o recarregamento dos dados do usuário
    const refreshUser = useCallback(() => {
        setRefreshTrigger(prev => prev + 1);
    }, []);

    const updateBalance = useCallback((newBalance: number) => {
        setUser(currentUser => currentUser ? { ...currentUser, balance: newBalance } : null);
    }, []);

    const updateToken = useCallback((newToken: string | null) => {
        setToken(newToken);
    }, []);

    useEffect(() => {
        async function loadUserFromToken() {
            if (token) {
                localStorage.setItem('token', token);
                try {
                    const response = await apiClient.get('/auth/me');
                    setUser(response.data);
                } catch (error) {
                    console.error("Falha ao buscar usuário com token:", error);
                    localStorage.removeItem('token');
                    setToken(null);
                    setUser(null);
                }
            } else {
                localStorage.removeItem('token');
            }
            setLoading(false);
        }
        loadUserFromToken();
    }, [token, refreshTrigger]); // ✨ CORREÇÃO: Adicionando o gatilho como dependência

    const login = async (email: string, password: string) => {
        try {
            const response = await apiClient.post('/auth/login', { email, password });
            setToken(response.data.token);
            return response.data;
        } catch (error: any) {
            // Propagar o erro completo para que a página possa detectar emailNotVerified
            throw error;
        }
    };

    const register = async (username: string, email: string, password: string) => {
        try {
            const response = await apiClient.post('/auth/register', { username, email, password });
            return response.data;
        } catch (error: any) {
            const errorMessage = error.response?.data?.error || 'Falha ao registrar.';
            throw new Error(errorMessage);
        }
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
    };

    const value = { user, token, loading, login, register, logout, updateBalance, refreshUser, updateToken }; // ✨ CORREÇÃO: Exportando a função

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}